import { useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import useAuthStore from '@/store/authStore';
import { registerSessionExpiredHandler } from '@/services/api';
import Providers from '@/app/providers';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppLayout from '@/layouts/AppLayout';
import { ErrorBoundary } from '@/lib/errors';
import LoadingScreen from '@/components/LoadingScreen';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const WhatsAppPage = lazy(() => import('@/pages/WhatsAppPage'));
const AgentConfigPage = lazy(() => import('@/pages/AgentConfigPage'));
const KnowledgeBasePage = lazy(() => import('@/pages/KnowledgeBasePage'));
const ConversationsPage = lazy(() => import('@/pages/ConversationsPage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const PlaygroundPage = lazy(() => import('@/pages/PlaygroundPage'));
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage'));
const CampaignWizardPage = lazy(() => import('@/pages/CampaignWizardPage'));
const CampaignDetailPage = lazy(() => import('@/pages/CampaignDetailPage'));
const ContactsPage = lazy(() => import('@/pages/ContactsPage'));
const TagsPage = lazy(() => import('@/pages/TagsPage'));
const TemplatesPage = lazy(() => import('@/pages/TemplatesPage'));
const TemplateEditorPage = lazy(() => import('@/pages/TemplateEditorPage'));
const ApiKeysPage = lazy(() => import('@/pages/ApiKeysPage'));
const PaymentCallbackPage = lazy(() => import('@/pages/PaymentCallbackPage'));

function SessionGuard() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const handledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleExpired = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    clearAuth();
    navigate(ROUTES.LOGIN, { replace: true });
    timerRef.current = setTimeout(() => { handledRef.current = false; }, 1000);
  }, [clearAuth, navigate]);

  useEffect(() => {
    registerSessionExpiredHandler(handleExpired);
    return () => { clearTimeout(timerRef.current); };
  }, [handleExpired]);

  return null;
}

function AppRoutes() {
  const { initAuth, isLoading } = useAuthStore();

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  if (isLoading) return <LoadingScreen />;

  return (
    <>
      <SessionGuard />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path={ROUTES.LOGIN} element={<ErrorBoundary><LoginPage /></ErrorBoundary>} />
          <Route path={ROUTES.ONBOARDING} element={
            <ProtectedRoute><ErrorBoundary><OnboardingPage /></ErrorBoundary></ProtectedRoute>
          } />
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
            <Route path={ROUTES.WHATSAPP} element={<WhatsAppPage />} />
            <Route path={ROUTES.AGENT_CONFIG} element={<AgentConfigPage />} />
            <Route path={ROUTES.KNOWLEDGE_BASE} element={<KnowledgeBasePage />} />
            <Route path={ROUTES.PLAYGROUND} element={<PlaygroundPage />} />
            <Route path={ROUTES.CONVERSATIONS} element={<ConversationsPage />} />
            <Route path={ROUTES.CAMPAIGNS} element={<CampaignsPage />} />
            <Route path={ROUTES.CAMPAIGN_NEW} element={<CampaignWizardPage />} />
            <Route path={ROUTES.CAMPAIGN_DETAIL_PATTERN} element={<CampaignDetailPage />} />
            <Route path={ROUTES.CONTACTS} element={<ContactsPage />} />
            <Route path={ROUTES.TAGS} element={<TagsPage />} />
            <Route path={ROUTES.TEMPLATES} element={<TemplatesPage />} />
            <Route path={ROUTES.TEMPLATE_NEW} element={<TemplateEditorPage />} />
            <Route path={ROUTES.TEMPLATE_EDIT_PATTERN} element={<TemplateEditorPage />} />
            <Route path={ROUTES.API_KEYS} element={<ApiKeysPage />} />
            <Route path={ROUTES.TEAM} element={<TeamPage />} />
            <Route path={ROUTES.ANALYTICS} element={<AnalyticsPage />} />
            <Route path={ROUTES.BILLING} element={<BillingPage />} />
            <Route path={ROUTES.PAYMENT_CALLBACK} element={<PaymentCallbackPage />} />
            <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <Providers>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </Providers>
  );
}

export default App;
